'use client';

import { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import ProductCard from './ProductCard';
import { Button } from '@/components';

interface Product {
  id: string;
  productId: string;
  covaLocationId?: number;
  packageId?: string;
  inStock?: number;
  unitCost?: number;
  product: {
    id: string;
    name: string;
    heroShotUri?: string;
    classificationName?: string;
    brandName?: string;
    slug?: string | null;
    shortDescription?: string | null;
    [key: string]: string | number | boolean | null | undefined;
  };
}

interface ProductSliderProps {
  title: string;
  products: Product[];
  priceMap: Record<string, number>;
  viewAllLink: string;
  storeId?: number;
}

export default function ProductSlider({ title, products, priceMap, viewAllLink, storeId }: ProductSliderProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    // Defer initial layout read to after the browser's first paint so
    // multiple ProductSlider instances on the home page don't chain forced
    // reflows during mount.
    const raf = requestAnimationFrame(checkScrollability);
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability, { passive: true });
      window.addEventListener('resize', checkScrollability);
      return () => {
        cancelAnimationFrame(raf);
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
    return () => cancelAnimationFrame(raf);
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section className="">
      <div className="mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h2-mobile md:text-h3-desktop font-bold text-text-primary">{title}</h2>
          <Link 
            href={viewAllLink}
            className="text-green-600 font-semibold text-sm transition-colors"
          >
            See All →
          </Link>
        </div>
        
        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <Button
              type="button"
              onClick={() => scroll('left')}
              variant="icon"
              color="white"
              size="icon-sm"
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 border border-neutral-20 p-2 shadow-lg"
              aria-label="Scroll left"
            >
              <ChevronLeftIcon size={20} className="w-5 h-5 text-text-primary" />
            </Button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {products.map((item) => {
              const price = priceMap[item.productId] || item.unitCost || 0;
              // Ensure item has all required ProductCard props
              const productItem = {
                id: item.id,
                productId: item.productId,
                covaLocationId: item.covaLocationId || storeId || 0,
                packageId: item.packageId || item.id,
                inStock: item.inStock || 1,
                unitCost: item.unitCost || price,
                product: item.product,
              };
              return (
                <div key={item.id} className="flex-shrink-0" style={{ width: 'calc((100% - 64px) / 4.5)' }}>
                  <ProductCard
                    item={productItem as any}
                    price={price}
                    onQuickView={() => {}}
                  />
                </div>
              );
            })}
          </div>

          {/* Right Arrow */}
          {canScrollRight && (
            <Button
              type="button"
              onClick={() => scroll('right')}
              variant="icon"
              color="white"
              size="icon-sm"
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 border border-neutral-20 p-2 shadow-lg"
              aria-label="Scroll right"
            >
              <ChevronRightIcon size={20} className="w-5 h-5 text-text-primary" />
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

